import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Download } from "lucide-react";

const TAX_RATE = 0.12;
const SERVICE_FEE = 99;

export default function Invoice() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load booking"));
  }, [bookingId]);

  if (err) return (
    <div className="p-10 text-center">
      <p className="text-slate-700">{err}</p>
      <Button onClick={() => navigate("/bookings")} variant="outline" className="mt-4 rounded-sm">Back to bookings</Button>
    </div>
  );
  if (!data) return <div className="p-10"><span className="hw-pulse-dot" /></div>;

  const { booking, hotel } = data;
  const total = booking.price;
  const discount = booking.discount || 0;
  // Reverse-compute base from total using breakup approximation used at checkout
  // total = (base - discount) + (base - discount) * TAX + SERVICE_FEE
  // => (base - discount) = (total - SERVICE_FEE) / (1 + TAX)
  const netRoom = Math.round((total - SERVICE_FEE) / (1 + TAX_RATE));
  const base = netRoom + discount;
  const taxes = Math.round(netRoom * TAX_RATE);
  const walletUsed = booking.wallet_used || 0;
  const netPaid = total - walletUsed;

  const doPrint = () => window.print();
  const invoiceNo = `INV-${(booking.booking_id || "").toUpperCase()}`;

  return (
    <div className="bg-slate-100 min-h-[calc(100vh-4rem)] py-8 print:bg-white print:py-0">
      {/* Toolbar (hidden on print) */}
      <div className="max-w-3xl mx-auto px-4 flex items-center justify-between print:hidden mb-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-700"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex gap-2">
          <Button onClick={doPrint} variant="outline" className="rounded-sm border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white" data-testid="invoice-print-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button onClick={doPrint} className="bg-blue-700 hover:bg-blue-800 text-white rounded-sm font-bold" data-testid="invoice-download-btn">
            <Download className="w-4 h-4 mr-1.5" /> Save PDF
          </Button>
        </div>
      </div>

      {/* Invoice sheet */}
      <div className="max-w-3xl mx-auto bg-white shadow-sm border border-slate-200 rounded-md p-8 md:p-12 print:shadow-none print:border-0" data-testid="invoice-sheet">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="hw-head text-3xl font-black text-slate-900">SwaanStay</div>
            <div className="text-sm text-slate-500 mt-1">Hotel bookings you can negotiate.</div>
            <div className="text-xs text-slate-500 mt-3">SwaanStay Technologies Pvt. Ltd.<br />Bengaluru, India · GSTIN: 29ABCDE1234F1Z5</div>
          </div>
          <div className="text-right">
            <div className="hw-label text-blue-700">Tax invoice</div>
            <div className="hw-head text-lg font-black text-slate-900 mt-1" data-testid="invoice-no">{invoiceNo}</div>
            <div className="text-xs text-slate-500 mt-1">Issued {new Date(booking.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
          </div>
        </div>

        <div className="my-6 h-px bg-slate-200" />

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="hw-label text-slate-500">Guest</div>
            <div className="hw-head font-bold text-slate-900 mt-1">{booking.customer_name}</div>
          </div>
          <div>
            <div className="hw-label text-slate-500">Hotel</div>
            <div className="hw-head font-bold text-slate-900 mt-1">{booking.hotel_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{hotel.address || hotel.city}</div>
            {hotel.gst && <div className="text-xs text-slate-500">GSTIN: {hotel.gst}</div>}
          </div>
          <div>
            <div className="hw-label text-slate-500">Check-in</div>
            <div className="text-slate-900 mt-1">{booking.checkin || "—"}</div>
          </div>
          <div>
            <div className="hw-label text-slate-500">Check-out</div>
            <div className="text-slate-900 mt-1">{booking.checkout || "—"}</div>
          </div>
          <div>
            <div className="hw-label text-slate-500">Rooms</div>
            <div className="text-slate-900 mt-1">{booking.rooms}</div>
          </div>
          <div>
            <div className="hw-label text-slate-500">Payment</div>
            <div className="text-slate-900 mt-1 capitalize">{booking.payment_method || "upi"}{booking.upi_txn_id ? ` · ${booking.upi_txn_id}` : ""}</div>
          </div>
        </div>

        <div className="my-6 h-px bg-slate-200" />

        {/* Charges */}
        <table className="w-full text-sm" data-testid="invoice-charges">
          <thead>
            <tr className="border-b-2 border-slate-900">
              <th className="text-left hw-label text-slate-700 py-2">Description</th>
              <th className="text-right hw-label text-slate-700 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100">
              <td className="py-3 text-slate-800">Room tariff ({booking.rooms} room{booking.rooms > 1 ? "s" : ""})</td>
              <td className="py-3 text-right font-semibold text-slate-900" data-testid="inv-base">₹{base.toLocaleString("en-IN")}</td>
            </tr>
            {discount > 0 && (
              <tr className="border-b border-slate-100">
                <td className="py-3 text-emerald-700">Coupon discount ({booking.coupon_code})</td>
                <td className="py-3 text-right font-semibold text-emerald-700" data-testid="inv-discount">− ₹{discount.toLocaleString("en-IN")}</td>
              </tr>
            )}
            <tr className="border-b border-slate-100">
              <td className="py-3 text-slate-800">GST (12%)</td>
              <td className="py-3 text-right font-semibold text-slate-900" data-testid="inv-tax">₹{taxes.toLocaleString("en-IN")}</td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-3 text-slate-800">Service fee</td>
              <td className="py-3 text-right font-semibold text-slate-900">₹{SERVICE_FEE.toLocaleString("en-IN")}</td>
            </tr>
            {walletUsed > 0 && (
              <tr className="border-b border-slate-100">
                <td className="py-3 text-slate-800">Wallet applied</td>
                <td className="py-3 text-right font-semibold text-slate-700">− ₹{walletUsed.toLocaleString("en-IN")}</td>
              </tr>
            )}
            <tr>
              <td className="pt-4 hw-head font-black text-slate-900 text-lg">Total paid</td>
              <td className="pt-4 text-right hw-head font-black text-slate-900 text-lg" data-testid="inv-total">₹{netPaid.toLocaleString("en-IN")}</td>
            </tr>
          </tbody>
        </table>

        <div className="my-8 h-px bg-slate-200" />

        <div className="text-xs text-slate-500 leading-relaxed">
          <p><strong className="text-slate-700">Cancellation:</strong> Free up to 48 hours before check-in. Post that, a one-night charge applies.</p>
          <p className="mt-1"><strong className="text-slate-700">Note:</strong> This is a computer-generated invoice and does not require a signature. For any queries, contact Support@swaanstay.com.</p>
          {booking.points_earned > 0 && (
            <p className="mt-2 text-blue-700"><strong>Rewards:</strong> You earned {booking.points_earned} points on this booking.</p>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between text-xs text-slate-400">
          <div>Booking ID: <span className="hw-mono">{booking.booking_id}</span></div>
          <div>Thank you for choosing SwaanStay.</div>
        </div>
      </div>
    </div>
  );
}
